// Top bar: section select (narrow), global search, critical-alert pill, browser↔server
// connection status, live clock, and account menu (logout).

import { useEffect } from 'react';
import { C, hexA, MONO, type StateToken } from '../lib/tokens';
import { fmtAge, fmtTS, fmtUTC } from '../lib/format';
import { activeCriticalCount } from '../lib/selectors';
import { useAuth, useData, useServerNow, useUi } from '../store/hooks';
import { navGo, uiStore } from '../store/uiStore';
import { logout } from '../store/authStore';
import { feed } from '../store/dataStore';
import { useNavDefs } from './Sidebar';
import { setSearchEl } from './searchRef';

function connToken(status: string, lastEventAt: number | null, offAt: number | null, now: number): [StateToken, string] {
  switch (status) {
    case 'live':
      return [['●', C.gn, 'Live'], lastEventAt ? fmtAge(now - lastEventAt) : ''];
    case 'offline':
      return [['✕', C.rd, 'Disconnected'], offAt ? 'since ' + fmtTS(offAt) : ''];
    case 'reconnecting':
    case 'connecting':
    default:
      return [['↻', C.am, 'Reconnecting'], ''];
  }
}

export function Header() {
  const narrow = useUi((s) => s.narrow);
  const nav = useUi((s) => s.nav);
  const q = useUi((s) => s.q);
  const tz = useUi((s) => s.tz);
  const feedStatus = useData((s) => s.feedStatus);
  const lastEventAt = useData((s) => s.lastEventAt);
  const offAt = useData((s) => s.offAt);
  const crit = useData((s) => activeCriticalCount(s.alerts));
  const user = useAuth((s) => s.user);
  const navDefs = useNavDefs();
  const now = useServerNow();

  useEffect(() => () => setSearchEl(null), []);

  const [token, sub] = connToken(feedStatus, lastEventAt, offAt, Date.now());
  const [glyph, color, label] = token;

  return (
    <header
      style={{
        height: 50,
        minHeight: 50,
        borderBottom: '1px solid ' + C.ln2,
        background: '#0E1524',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 14px',
      }}
    >
      {narrow && (
        <select
          value={nav}
          onChange={(e) => navGo(e.target.value as never)}
          aria-label="Section"
          style={{ background: '#121B2C', border: '1px solid ' + C.ln, color: C.tx, borderRadius: 6, height: 32, fontSize: 12, padding: '0 6px' }}
        >
          {navDefs.map((n) => (
            <option key={n.key} value={n.key}>
              {n.label}
            </option>
          ))}
        </select>
      )}

      <input
        ref={setSearchEl}
        value={q}
        onChange={(e) => uiStore.set({ q: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') navGo(nav === 'positions' ? 'positions' : 'accounts');
        }}
        placeholder="Search label, login, broker, symbol, magic…  ( / )"
        aria-label="Global search"
        style={{
          flex: 1,
          maxWidth: 430,
          height: 32,
          background: '#0A0F19',
          border: '1px solid ' + C.ln,
          borderRadius: 6,
          color: C.tx,
          fontSize: 12.5,
          padding: '0 12px',
        }}
      />
      <div style={{ flex: 1 }} />

      {crit > 0 && (
        <button
          onClick={() => uiStore.set({ nav: 'alerts', aSev: 'critical', aState: '' })}
          title="Active critical alerts — open Alerts"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 30,
            padding: '0 10px',
            background: hexA(C.rd, 0.12),
            border: '1px solid ' + hexA(C.rd, 0.4),
            borderRadius: 6,
            color: C.rd,
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          ◆ {crit} critical
        </button>
      )}

      <div
        role="status"
        title={'Browser ↔ server connection: ' + label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 30,
          padding: '0 10px',
          borderRadius: 6,
          fontSize: 11.5,
          fontWeight: 600,
          color,
          background: hexA(color, 0.1),
          border: '1px solid ' + hexA(color, 0.35),
        }}
      >
        <span style={{ fontFamily: MONO }} aria-hidden="true">
          {glyph}
        </span>
        <span>{label}</span>
        {sub && <span style={{ fontWeight: 400, color: C.dm, fontFamily: MONO, fontSize: 10.5 }}>{sub}</span>}
      </div>

      {!narrow && (
        <div style={{ textAlign: 'right', fontFamily: MONO, fontSize: 10.5, color: C.dm, lineHeight: 1.5 }}>
          Local {fmtTS(now)}
          {tz !== 'local' && (
            <>
              <br />
              UTC {fmtUTC(now)}
            </>
          )}
        </div>
      )}

      <button
        onClick={() => {
          feed.stop();
          void logout();
        }}
        title={user ? user.email + ' · sign out' : 'Sign out'}
        style={{
          height: 32,
          padding: '0 10px',
          background: '#121B2C',
          border: '1px solid ' + C.ln,
          borderRadius: 6,
          color: C.dm,
          fontSize: 11.5,
          fontFamily: 'inherit',
        }}
      >
        ⎋ Sign out
      </button>
    </header>
  );
}
